// La vista se calcula, nunca se guarda (§2, principio 1):
//   vista = confirmado(servidor) + reaplicar(pendientes)
// Un cambio del servidor reemplaza la base; los pendientes se reaplican encima.

import { applySetDelta } from "./sets";
import type { Op, Row } from "./types";

/** Fila de la vista: la confirmada con los cambios pendientes aplicados. */
export interface ViewRow extends Row {
  /** Tiene operaciones que el servidor aún no confirmó. */
  pending: boolean;
}

/**
 * Aplica una operación a una fila. `undefined` = la fila no existe (borrada,
 * o un cambio sobre una fila que el servidor ya borró).
 */
export function applyOp(row: Row | undefined, op: Op): Row | undefined {
  if (op.kind === "delete") return undefined;
  if (!row && op.kind !== "create") return undefined;
  const base: Row = row ?? { id: op.rowId, values: {}, fieldVersions: {} };
  const values = { ...base.values };
  for (const [key, value] of Object.entries(op.set ?? {})) {
    if (value === null || value === undefined) delete values[key];
    else values[key] = value;
  }
  for (const [key, delta] of Object.entries(op.sets ?? {})) {
    const next = applySetDelta(values[key], delta);
    if (next === undefined) delete values[key];
    else values[key] = next;
  }
  return { ...base, values };
}

/** Fila de la vista a partir de la confirmada y sus operaciones pendientes, en orden. */
export function viewRow(confirmed: Row | undefined, ops: readonly Op[]): ViewRow | undefined {
  let row = confirmed;
  for (const op of ops) row = applyOp(row, op);
  return row ? { ...row, pending: ops.length > 0 } : undefined;
}

/**
 * Vista completa de un alcance. Las filas confirmadas conservan su orden; las
 * creadas sin conexión van al final, en el orden en que se crearon.
 */
export function viewRows(confirmed: ReadonlyMap<string, Row>, ops: readonly Op[]): ViewRow[] {
  const byRow = new Map<string, Op[]>();
  for (const op of ops) {
    const list = byRow.get(op.rowId);
    if (list) list.push(op);
    else byRow.set(op.rowId, [op]);
  }
  const out: ViewRow[] = [];
  for (const [id, row] of confirmed) {
    const next = viewRow(row, byRow.get(id) ?? []);
    if (next) out.push(next);
  }
  for (const [id, rowOps] of byRow) {
    if (confirmed.has(id)) continue;
    const next = viewRow(undefined, rowOps);
    if (next) out.push(next);
  }
  return out;
}
